const MAX_HEADING_LEVEL = 6;
const HEADING_STYLE_NAME_PATTERN = new RegExp(`^heading ([1-${MAX_HEADING_LEVEL}])$`, 'i');

export interface DocxNode {
  type?: string;
  styleId?: string | null;
  styleName?: string | null;
  numbering?: unknown;
  isBold?: boolean | null;
  styleRunIsBold?: boolean | null;
  fontSize?: number | null;
  value?: string;
  children?: DocxNode[];
}

export const inheritStyleBold = <T extends DocxNode>(node: T): T => {
  if (node.type === 'paragraph' && node.styleRunIsBold && !HEADING_STYLE_NAME_PATTERN.test(node.styleName ?? '')) {
    const boldRuns = (child: DocxNode) => {
      if (child.type === 'run' && child.isBold == null) {
        child.isBold = true;
      }
      child.children?.forEach(boldRuns);
    };
    node.children?.forEach(boldRuns);
  }
  node.children?.forEach(inheritStyleBold);
  return node;
};

const collectByType = (node: DocxNode, type: string, found: DocxNode[] = []): DocxNode[] => {
  if (node.type === type) {
    found.push(node);
    return found;
  }

  node.children?.forEach((child) => collectByType(child, type, found));
  return found;
};

const textContentOf = (node: DocxNode): string =>
  node.type === 'text' ? (node.value ?? '') : (node.children ?? []).map(textContentOf).join('');

const textLength = (node: DocxNode): number => textContentOf(node).length;

const isHeadingStyled = (paragraph: DocxNode) => /^heading \d/i.test(paragraph.styleName ?? '');

// mammoth only surfaces sizes set directly on runs; sizes inherited from styles or document
// defaults come through as null, leaving nothing to compare against — the heuristic stays off.
const bodyFontSize = (bodyRuns: DocxNode[]): number | null => {
  const weights = new Map<number, number>();

  bodyRuns.forEach((run) => {
    if (run.fontSize != null) {
      weights.set(run.fontSize, (weights.get(run.fontSize) ?? 0) + textLength(run));
    }
  });

  let bodySize: number | null = null;

  weights.forEach((weight, size) => {
    const bodyWeight = bodySize == null ? -1 : weights.get(bodySize)!;

    if (weight > bodyWeight || (weight === bodyWeight && size > bodySize!)) {
      bodySize = size;
    }
  });

  return bodySize;
};

export const detectUnstyledHeadings = <T extends DocxNode>(document: T): T => {
  const candidates: { paragraph: DocxNode; runs: DocxNode[]; size: number }[] = [];
  const bodyRuns: DocxNode[] = [];
  let hasSizedNonCandidateRun = false;

  collectByType(document, 'paragraph').forEach((paragraph) => {
    if (isHeadingStyled(paragraph)) {
      return;
    }

    const runs = collectByType(paragraph, 'run').filter((run) => textLength(run) > 0);

    if (runs.length === 0) {
      return;
    }

    const size = runs[0].fontSize;
    const isHeadingShaped =
      !/^[a-z]/.test(textContentOf(paragraph).trimStart()) && collectByType(paragraph, 'hyperlink').length === 0;
    const isBoldUniformSizedLine =
      !paragraph.numbering &&
      isHeadingShaped &&
      size != null &&
      runs.every((run) => run.isBold && run.fontSize === size);

    if (isBoldUniformSizedLine) {
      candidates.push({ paragraph, runs, size });
    } else {
      hasSizedNonCandidateRun ||= runs.some((run) => run.fontSize != null);
    }

    bodyRuns.push(...runs);
  });

  const bodySize = bodyFontSize(bodyRuns);

  if (bodySize == null || !hasSizedNonCandidateRun) {
    return document;
  }

  const headings = candidates.filter(({ size }) => size > bodySize);
  const rankedSizes = [...new Set(headings.map(({ size }) => size))].sort((a, b) => b - a);

  headings.forEach(({ paragraph, runs, size }) => {
    const level = Math.min(MAX_HEADING_LEVEL, rankedSizes.indexOf(size) + 1);
    paragraph.styleId = `Heading${level}`;
    paragraph.styleName = `Heading ${level}`;
    runs.forEach((run) => {
      run.isBold = false;
    });
  });

  return document;
};

interface MammothElement {
  type?: string;
  numbering?: unknown;
  spacing?: { before?: string | null; after?: string | null } | null;
  children?: MammothElement[];
}

// 240 twips = one 12pt line; Word's default 8pt space-after stays below the threshold.
const TWIPS_PER_BLANK_LINE = 240;
const MAX_BLANK_LINES = 4;

const blankLineCount = (twips: string | null | undefined): number => {
  const value = Number(twips);

  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }

  return Math.min(MAX_BLANK_LINES, Math.floor(value / TWIPS_PER_BLANK_LINE));
};

const blankParagraphs = (count: number): MammothElement[] =>
  Array.from({ length: count }, () => ({ type: 'paragraph', children: [] }));

// The editor has no paragraph margins, so each full line of w:spacing becomes a real empty
// paragraph. Upstream style resolution is single-level: basedOn chains and Normal are not consulted.
export const expandSpacingToBlankParagraphs = (element: MammothElement): MammothElement => {
  if (element.children?.length) {
    element.children.forEach(expandSpacingToBlankParagraphs);

    // blank paragraphs inside table cells or between list items add noise, not fidelity
    if (element.type !== 'tableCell') {
      element.children = element.children.flatMap((child) =>
        child.type === 'paragraph' && !child.numbering
          ? [
              ...blankParagraphs(blankLineCount(child.spacing?.before)),
              child,
              ...blankParagraphs(blankLineCount(child.spacing?.after)),
            ]
          : [child],
      );
    }
  }

  return element;
};

const OOXML_ALIGNMENT_TO_CSS: Record<string, string> = {
  center: 'center',
  right: 'right',
  end: 'right',
  both: 'justify',
  distribute: 'justify',
};

const CSS_ALIGNMENTS = ['none', 'center', 'right', 'justify'];

// Word's default indent step is 720 twips (0.5"); the editor's Indent extension counts in levels.
const TWIPS_PER_INDENT_LEVEL = 720;
const MAX_INDENT_LEVEL = 8;

const syntheticStyleName = (alignment: string, indentLevel: number, hangingLevel: number, headingLevel?: string) =>
  `__fmt_${alignment}_${indentLevel}${hangingLevel > 0 ? `_hang${hangingLevel}` : ''}${headingLevel ? `_h${headingLevel}` : ''}`;

const formattingClass = (alignment: string, indentLevel: number, hangingLevel: number) =>
  `fmt-${alignment}-${indentLevel}${hangingLevel > 0 ? `-hang${hangingLevel}` : ''}`;

interface MammothParagraph {
  type?: string;
  alignment?: string | null;
  indent?: { start?: string | null; hanging?: string | null } | null;
  styleId?: string | null;
  styleName?: string | null;
  numbering?: unknown;
  children?: MammothParagraph[];
}

const indentLevelOf = (twips: string | null | undefined): number => {
  const value = Number(twips);

  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }

  return Math.min(MAX_INDENT_LEVEL, Math.round(value / TWIPS_PER_INDENT_LEVEL));
};

// w:ind hanging is how far left the first line sits from the text column; snap it to indent
// levels and never past the left indent, so the first line can return to but not cross the margin.
const hangingLevelOf = (paragraph: MammothParagraph, indentLevel: number): number => {
  const hangingTwips = Number(paragraph.indent?.hanging);

  if (indentLevel <= 0 || !Number.isFinite(hangingTwips) || hangingTwips <= 0) {
    return 0;
  }

  return Math.min(indentLevel, Math.round(hangingTwips / TWIPS_PER_INDENT_LEVEL));
};

const HEADING_LEVELS = Array.from({ length: MAX_HEADING_LEVEL }, (_, index) => index + 1);

// mammoth deliberately discards w:jc and w:ind as non-semantic, but the editor supports
// text-align and indent levels. Smuggle them through mammoth as synthetic style names,
// map them to CSS classes, then rewrite to the attributes the editor parses.
export const preserveFormattingTransform = (element: MammothParagraph): MammothParagraph => {
  if (element.type === 'paragraph' && !element.numbering) {
    const alignment = (element.alignment && OOXML_ALIGNMENT_TO_CSS[element.alignment]) || 'none';
    const indentLevel = indentLevelOf(element.indent?.start);
    const headingLevel = HEADING_STYLE_NAME_PATTERN.exec(element.styleName ?? '')?.[1];
    // headings drop indent metadata downstream, so never encode a hang for them
    const hangingLevel = headingLevel ? 0 : hangingLevelOf(element, indentLevel);

    if (alignment !== 'none' || indentLevel > 0) {
      element.styleId = null;
      element.styleName = syntheticStyleName(alignment, indentLevel, hangingLevel, headingLevel);
    }
  }

  element.children?.forEach(preserveFormattingTransform);

  return element;
};

const INDENT_LEVELS = Array.from({ length: MAX_INDENT_LEVEL + 1 }, (_, level) => level);

export const formattingStyleMap = CSS_ALIGNMENTS.flatMap((alignment) =>
  INDENT_LEVELS.flatMap((indentLevel) => {
    if (alignment === 'none' && indentLevel === 0) {
      return [];
    }

    const hangingLevels = indentLevel > 0 ? INDENT_LEVELS.slice(0, indentLevel + 1) : [0];

    const paragraphRules = hangingLevels.map(
      (hangingLevel) =>
        `p[style-name='${syntheticStyleName(alignment, indentLevel, hangingLevel)}'] => p.${formattingClass(alignment, indentLevel, hangingLevel)}:fresh`,
    );

    const headingRules = HEADING_LEVELS.map(
      (level) =>
        `p[style-name='${syntheticStyleName(alignment, indentLevel, 0, String(level))}'] => h${level}.${formattingClass(alignment, indentLevel, 0)}:fresh`,
    );

    return [...paragraphRules, ...headingRules];
  }),
);

export const applyFormattingStyles = (doc: Document): void => {
  const formatted = doc.body.querySelectorAll<HTMLElement>('[class*="fmt-"]');

  formatted.forEach((element) => {
    const match = /(?:^|\s)fmt-(none|center|right|justify)-(\d+)(?:-hang(\d+))?(?:\s|$)/.exec(element.className);

    if (match) {
      const [, alignment, indentLevel, hangingLevelStr] = match;
      const level = Number(indentLevel);
      const hangingLevel = Number(hangingLevelStr ?? 0);
      // A heading carries its level in the tag; the editor's Indent extension doesn't cover
      // headings, so stamping a same-named `level` attribute here only corrupts the heading.
      const isHeading = /^H[1-6]$/.test(element.tagName);

      if (alignment !== 'none') {
        element.style.textAlign = alignment;
      }

      // indent styles are left to the editor's Indent extension, which regenerates them
      // from these attributes on parse
      if (level > 0 && !isHeading) {
        element.setAttribute('level', String(level));
        element.setAttribute('indent', 'true');

        if (hangingLevel > 0) {
          element.setAttribute('hanging', String(hangingLevel));
        }
      }
    }

    element.removeAttribute('class');
  });
};
